import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useClient } from '../../contexts/ClientContext';
import Spinner from '../UI/Spinner'; // Ensure Spinner is imported

// SVG Icons as inline components (avoiding react-icons dependency)
const MailOpenIcon: React.FC<{ className?: string }> = ({ className }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M21.75 9v.906a2.25 2.25 0 01-1.183 1.981l-6.478 3.488M2.25 9v.906a2.25 2.25 0 001.183 1.981l6.478 3.488m8.839 2.51l-4.66-2.51m0 0l-1.023-.55a2.25 2.25 0 00-2.134 0l-1.022.55m0 0l-4.661 2.51m16.5 1.615a2.25 2.25 0 01-2.25 2.25h-15a2.25 2.25 0 01-2.25-2.25V8.844a2.25 2.25 0 011.183-1.981l7.5-4.039a2.25 2.25 0 012.134 0l7.5 4.039a2.25 2.25 0 011.183 1.98V19.5z" />
  </svg>
);

const ClockIcon: React.FC<{ className?: string }> = ({ className }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M12 6v6h4.5m4.5 0a9 9 0 11-18 0 9 9 0 0118 0z" />
  </svg>
);

const LogoutIcon: React.FC<{ className?: string }> = ({ className }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 9V5.25A2.25 2.25 0 0013.5 3h-6a2.25 2.25 0 00-2.25 2.25v13.5A2.25 2.25 0 007.5 21h6a2.25 2.25 0 002.25-2.25V15m3 0l3-3m0 0l-3-3m3 3H9" />
  </svg>
);

const RefreshIcon: React.FC<{ className?: string }> = ({ className }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M16.023 9.348h4.992v-.001M2.985 19.644v-4.992m0 0h4.992m-4.993 0l3.181 3.183a8.25 8.25 0 0013.803-3.7M4.031 9.865a8.25 8.25 0 0113.803-3.7l3.181 3.182m0-4.991v4.99" />
  </svg>
);

/**
 * Shown to authenticated users who don't have access to any workspace.
 * This could happen when:
 * - A user signed up without an invitation link
 * - A user was removed from all workspaces
 * - A user's invitation hasn't been processed yet
 */
const NoWorkspaceAccess: React.FC = () => {
  const { currentUser, logout } = useAuth();
  const { refreshAccessibleClients, acceptInvitation, isLoading } = useClient();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const inviteToken = searchParams.get('invite');

  const [isRefreshing, setIsRefreshing] = useState(false);
  const [isAcceptingInvite, setIsAcceptingInvite] = useState(false);
  const [inviteError, setInviteError] = useState<string | null>(null);
  const [lastRefresh, setLastRefresh] = useState<Date | null>(null);

  // Auto-accept invitation if token is present
  useEffect(() => {
    const processInvite = async () => {
      if (inviteToken && !isAcceptingInvite) {
        setIsAcceptingInvite(true);
        setInviteError(null);
        try {
          const result = await acceptInvitation(inviteToken);
          if (result.success) {
            // Success! The context will update accessibleClients
            // and DashboardLayout will automatically unmount this component
            console.log("Invitation accepted, workspace access granted.");
          } else {
            setInviteError(result.message);
          }
        } catch (err) {
          console.error("Error accepting invite:", err);
          setInviteError("Failed to accept invitation.");
        } finally {
          setIsAcceptingInvite(false);
        }
      }
    };

    processInvite();
  }, [inviteToken, acceptInvitation]); // eslint-disable-line react-hooks/exhaustive-deps

  // Auto-refresh every 30 seconds
  useEffect(() => {
    const interval = setInterval(() => {
      handleRefresh();
    }, 30000);

    return () => clearInterval(interval);
  }, []);

  const handleRefresh = async () => {
    setIsRefreshing(true);
    try {
      await refreshAccessibleClients();
      setLastRefresh(new Date());
    } finally {
      setIsRefreshing(false);
    }
  };

  const handleLogout = async () => {
    try {
      await logout();
      navigate('/login');
    } catch (error) {
      console.error('Logout error:', error);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 flex items-center justify-center p-4">
      <div className="max-w-md w-full">
        {/* Main Card */}
        <div className="bg-white rounded-2xl shadow-lg p-8 text-center">
          {/* Icon */}
          <div className="w-20 h-20 bg-yellow-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <MailOpenIcon className="w-10 h-10 text-yellow-600" />
          </div>

          {/* Title */}
          <h1 className="text-2xl font-bold text-gray-800 mb-2">
            Waiting for Workspace Access
          </h1>

          {/* User Info */}
          <p className="text-gray-500 mb-6">
            Signed in as <span className="font-medium text-gray-700">{currentUser?.email}</span>
          </p>

          {/* Message */}
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-6">
            <div className="flex items-start text-left">
              <ClockIcon className="w-5 h-5 text-yellow-600 mt-0.5 mr-3 flex-shrink-0" />
              <div>
                <p className="text-sm text-yellow-800 font-medium mb-1">
                   {isAcceptingInvite ? 'Processing Invitation...' : inviteError ? 'Invitation Error' : 'No Workspace Access Yet'}
                </p>
                <p className="text-sm text-yellow-700">
                  {isAcceptingInvite 
                    ? 'Please wait while we link your account to the workspace.' 
                    : inviteError 
                      ? inviteError
                      : "You need to be invited to a workspace before you can access the dashboard. If you've received an invitation email, please click the link in that email to accept it."}
                </p>
              </div>
            </div>
          </div>
          
           {isAcceptingInvite && (
             <div className="mb-6 flex justify-center">
               <Spinner />
             </div>
          )}

          {/* What to do */}
          <div className="text-left mb-6">
            <h3 className="font-medium text-gray-800 mb-2">What to do:</h3>
            <ul className="text-sm text-gray-600 space-y-2">
              <li className="flex items-start">
                <span className="w-5 h-5 bg-blue-100 rounded-full flex items-center justify-center text-xs font-medium text-blue-600 mr-2 flex-shrink-0 mt-0.5">1</span>
                Check your email for an invitation link
              </li>
              <li className="flex items-start">
                <span className="w-5 h-5 bg-blue-100 rounded-full flex items-center justify-center text-xs font-medium text-blue-600 mr-2 flex-shrink-0 mt-0.5">2</span>
                Click the invitation link to join a workspace
              </li>
              <li className="flex items-start">
                <span className="w-5 h-5 bg-blue-100 rounded-full flex items-center justify-center text-xs font-medium text-blue-600 mr-2 flex-shrink-0 mt-0.5">3</span>
                Contact your administrator if you haven't received an invitation
              </li>
            </ul>
          </div>

          {/* Auto-refresh notice */}
          <div className="text-xs text-gray-400 mb-4">
            {lastRefresh && (
              <span>Last checked: {lastRefresh.toLocaleTimeString()}</span>
            )}
            {' • '}
            Auto-refreshing every 30 seconds
          </div>

          {/* Actions */}
          <div className="space-y-3">
             {!isAcceptingInvite && (
             <button
              onClick={handleRefresh}
              disabled={isRefreshing || isLoading}
              className="w-full py-3 px-4 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 transition-colors"
            >
              <RefreshIcon className={`w-5 h-5 ${isRefreshing ? 'animate-spin' : ''}`} />
              {isRefreshing ? 'Checking...' : 'Check for Access Now'}
            </button>
             )}

            <button
              onClick={handleLogout}
              className="w-full py-3 px-4 bg-gray-100 text-gray-700 rounded-lg font-medium hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2 flex items-center justify-center gap-2 transition-colors"
            >
              <LogoutIcon className="w-5 h-5" />
              Sign Out
            </button>
          </div>
        </div>

        {/* Footer */}
        <p className="text-center text-xs text-gray-400 mt-4">
          If you believe this is an error, please contact support.
        </p>
      </div>
    </div>
  );
};

export default NoWorkspaceAccess;
